import { Button, Alert, Card, Menu, Popover, message } from '@nackle/paper';
import { Pencil, ClipboardEmpty, Trash } from '@nackle/icons';
import { Menu3 } from '@nackle/icons/dist/react-es/ultimate';
import React, { Component } from 'react';
import { CardHeader } from 'shared/card-header';
import { confirm } from 'shared/dialogs';
import { HasPermission } from 'shared/HasPermission';
import { Room } from 'shared/interfaces';
import { copyValueToClipboard } from 'utils/clipboard';
import { defined } from 'utils/defined';

interface Props  {
    room: Room;
    onDelete?: (id: string) => void;
    onEdit: (roomId: string) => void;
}

export class ArcadeRoomListEntry extends Component<Props> {
    // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
    public render() {
        const {
            onDelete,
            room,
        } = this.props;

        const editButton = (
            <HasPermission app="arcade-api" permission="edit-room">
                {(hasPermission: boolean) =>
                    <Menu.Item
                        data-room-id={room.id}
                        disabled={!hasPermission}
                        icon={<Pencil/>}
                        onClick={this.handleEdit}
                        title="Edit Room"
                    >
                        Edit Room
                    </Menu.Item>
                }
            </HasPermission>
        );

        const copyIdButton = (
            <Menu.Item
                icon={<ClipboardEmpty/>}
                onClick={this.handleCopyIdToClipboard}
                title="Copy Room ID"
            >
                Copy Room ID
            </Menu.Item>
        );

        const deleteButton = onDelete && (
            <HasPermission app="arcade-api" permission="delete-room">
                {(hasPermission: boolean) =>
                <Menu>
                    <Menu.Item
                        data-open-modal-button-id={room.id}
                        disabled={!hasPermission}
                        icon={<Trash/>}
                        onClick={this.handleDelete}
                        id={`open-modal-btn-${room.id}`}
                        title="Delete"
                        danger={true}
                    >
                        Delete
                    </Menu.Item>
                </Menu>
                }
            </HasPermission>
        );

        const cardMenu = (
            <Popover trigger="click" destroyTooltipOnHide={true} mouseLeaveDelay={0.2}
            autoAdjustOverflow
                content={
                    <Menu>
                        {editButton}
                        {copyIdButton}
                        {deleteButton}
                    </Menu>
                }
            >
                <Button
                    icon={<Menu3/>}
                    id={`menu-btn-${room.id}`}
                    className='menu-btn-cabinet'
                    size="small"
                />
            </Popover>
        );

        return (
            <Card
                key={`room-list-${room.id}`}
                data-room-id={room.id}
            >
                <CardHeader
                    className="arcade-game-name"
                    right={cardMenu}
                    title={room.name}
                />

                { room.freePlay && <Alert style={{ marginBottom: 10 }} message="Instant Play Room"></Alert>}

                <ul>
                    { room.cabinets.map(cabinet =>
                        <li key={`room-list-cabinet-${cabinet.cabinetId}`}>{cabinet.name}</li>
                    ) }
                </ul>
            </Card>
        );
    }

    private handleEdit = () => this.props.onEdit(this.props.room.id);

    private handleCopyIdToClipboard = () => {
        // Tell the user we just messed with their clipboard. The fact that we're including the literal Room ID in the
        // message should be a good fallback if it didn't work for some reason
        if (copyValueToClipboard(this.props.room.id)) {
            message.success({
                icon: <ClipboardEmpty/>,
                content: `Copied Room ID ${JSON.stringify(this.props.room.id)} to clipboard`,
            });
        } else {
            message.error({
                icon: <ClipboardEmpty/>,
                content: `Failed to copy Room ID. Room ID is ${JSON.stringify(this.props.room.id)}`,
            });
        }
    };

    private handleDelete = async () => {
        const { room } = this.props;

        confirm({
            message: <p>Are you sure you want to delete the arcade game room <strong>{room.name}</strong>?</p>,
            modalId: room.id,
            primaryButtonText: 'Delete',
            onConfirm: () => defined(this.props.onDelete)(room.id),
        });
    };
}
